import { useState, useEffect } from 'react';
import { companies } from '../api/index';

const CACHE_KEY = 'default_pagination_limit';

/**
 * Last-resort page size, used only when the company has no setting and
 * VITE_PAGINATION is unset. Kept in step with the seeded company default in
 * the API's default-company-template.json and with DEFAULT_ORDER_PAGE_SIZE.
 */
const DEFAULT_PAGE_SIZE = 20;

/**
 * One in-flight request per page load, shared by every grid.
 *
 * Each grid page calls this hook on mount, and the hook used to issue its own
 * `getShippingConfig()` every time — so moving Products -> Orders -> Purchase
 * Orders fetched the same unchanging number three times.
 */
let configPromise: Promise<number | null> | null = null;

const loadDefaultPageSize = (): Promise<number | null> => {
  configPromise ??= companies
    .getShippingConfig()
    .then((config: any) => config?.defaultPageSize ?? null)
    .catch(() => {
      // Cleared so a later mount can retry rather than inheriting the failure
      // for the rest of the session.
      configPromise = null;
      return null;
    });
  return configPromise;
};

/** Clears the cached page size. Call on logout: the next user's company may set a different one. */
export const resetPaginationConfig = () => {
  configPromise = null;
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // Private mode / blocked storage — the value is a convenience, not state.
  }
};

const readCachedLimit = (): number | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    const parsed = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Resolves the default page size for grids in priority order:
 *  1. Company-level setting: shippingConfig.defaultPageSize
 *  2. VITE_PAGINATION environment variable
 *  3. Hard-coded fallback: DEFAULT_PAGE_SIZE
 *
 * `ready` exists so a grid does not fire its first request at one page size and
 * then immediately fire it again at another. It is true from the first render
 * whenever a cached value exists, which — since the cache now lives in
 * localStorage rather than sessionStorage — means every visit after the user's
 * first. That matters because the grids gate their own fetch on it: while this
 * was a per-session cache, opening a new tab put a config round-trip in front
 * of the orders query instead of alongside it, and the list could not start
 * loading until it came back.
 *
 * The request still goes out on mount to revalidate; if the company's value has
 * changed, `limit` updates and the grid refetches once.
 */
export function usePaginationConfig() {
  const envDefault =
    parseInt(import.meta.env.VITE_PAGINATION || String(DEFAULT_PAGE_SIZE), 10) ||
    DEFAULT_PAGE_SIZE;

  const cachedLimit = readCachedLimit();
  const [limit, setLimit] = useState(cachedLimit ?? envDefault);
  const [ready, setReady] = useState(true);

  useEffect(() => {
    let cancelled = false;

    loadDefaultPageSize()
      .then((pageSize) => {
        if (cancelled) return;
        if (pageSize) {
          // Only when it actually differs: setting it unconditionally would
          // change `limit`'s identity and retrigger every grid's fetch effect.
          setLimit((current) => (current === pageSize ? current : pageSize));
          try {
            localStorage.setItem(CACHE_KEY, String(pageSize));
          } catch {
            // Non-fatal: we just lose the head start on the next visit.
          }
        }
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { limit, setLimit, ready };
}
